'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuX } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { Box, Flex, Text } from '@chakra-ui/react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'

import { getCookie, setCookies } from 'app/serverActions/cookies'
import { revalidatePathOnDemand } from 'app/serverActions/routes'
import { clientSignOut, CustomerInfo } from 'app/utils/authentication'
import { SearchForProducts } from 'components/form/SearchForProducts'
import { MenuButton } from 'components/nav/MenuButton'
import { NewsletterModal } from 'components/partials/shared/Newsletter/NewsletterModal'
import { Button, ButtonWhite } from 'components/ui/Buttons/Button'
import { Divider } from 'components/ui/Divider'
import { AppDispatch } from 'store'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'
import { DEFAULT_LANG, I18_COOKIE_NAME, STORAGE_KEYS } from 'utils/constants'
import i18n from 'utils/i18n'
import { saveToStorage } from 'utils/storage'
import { useClickOutside } from 'utils/useClickOutside'

import { StyledLink, StyledMenu } from './styles'

type Props = {
  isLoggedIn: boolean
  isCartPreviewOpen: boolean
  customerInfo: CustomerInfo
}

const routes = [
  {
    label: 'home',
    link: '/',
    isProtected: false,
  },
  {
    label: 'browseProducts',
    link: '/browse',
    isProtected: false,
  },
  {
    label: 'productBundles',
    link: '/bundles',
    isProtected: false,
  },
  {
    label: 'projectIdeas',
    link: '/articles',
    isProtected: false,
  },
  {
    label: 'contactUs',
    link: '/contact',
    isProtected: false,
  },
  {
    label: 'myRentals',
    link: '/account/rentals',
    isProtected: true,
  },
  {
    label: 'myAccount',
    link: '/account',
    isProtected: true,
  },
]

const Menu: React.FC<Props> = ({ isLoggedIn, isCartPreviewOpen, customerInfo }) => {
  const menuRef = useRef(null)
  const dispatch = useDispatch<AppDispatch>()
  const { t } = useTranslation('common')
  const router = useRouter()
  const pathname = usePathname()

  const isUserInNewsletter = customerInfo.customer?.newsletterSubscription ?? false

  const [language, setLanguage] = useState(DEFAULT_LANG)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false)

  useEffect(() => {
    if (isCartPreviewOpen) {
      setIsMenuOpen(false)
    }
  }, [isCartPreviewOpen])

  useEffect(() => {
    const body = document.querySelector('body')
    if (isMenuOpen) {
      body && body.classList.add('scroll-blocked')
    } else {
      body && body.classList.remove('scroll-blocked')
    }
  }, [isMenuOpen])

  useEffect(() => {
    prepareLanguage()
  }, [])

  useClickOutside(menuRef, (event: MouseEvent) => {
    const isClickOnMenuButton = (event.target as HTMLElement).dataset.ismenubutton

    if (!isClickOnMenuButton) {
      setIsMenuOpen(false)
    }
  })

  const prepareLanguage = async () => {
    const lng = (await getCookie(I18_COOKIE_NAME)) ?? DEFAULT_LANG
    setLanguage(lng)
    i18n.changeLanguage(lng ?? DEFAULT_LANG)
  }

  const handleLanguageChange = async (lng: string) => {
    setLanguage(lng)
    setIsMenuOpen(false)

    saveToStorage(STORAGE_KEYS.language, lng)
    await setCookies(I18_COOKIE_NAME, lng)

    router.refresh()

    i18n.changeLanguage(lng)
  }

  const logout = async () => {
    const result = await clientSignOut()
    if (result) {
      setIsMenuOpen(false)
      checkForIntercomAndShutdown()
      await revalidatePathOnDemand(pathname ?? '')
      // window.location.reload()
    } else {
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

  const checkForIntercomAndShutdown = () => {
    // Intercom is added directly through GTM so we have access to all methods
    if (window.Intercom) {
      window.Intercom('shutdown')
    }
  }

  const handleNewsletterLinkClick = () => {
    setIsMenuOpen(false)
    setIsNewsletterModalOpen(true)
  }

  return (
    <>
      <NewsletterModal isOpen={isNewsletterModalOpen} onClose={() => setIsNewsletterModalOpen(false)} />
      <Box className="gtm-menu-toggle">
        <MenuButton isOpen={isMenuOpen} onClickHandler={() => setIsMenuOpen(!isMenuOpen)} />

        {isMenuOpen && (
          <StyledMenu ref={menuRef}>
            <Flex justifyContent="flex-end" display={{ base: 'flex', lg: 'none' }}>
              <button onClick={() => setIsMenuOpen(false)} style={{ cursor: 'pointer' }}>
                <LuX size={24} />
              </button>
            </Flex>
            <Box marginTop="30px" display={{ lg: 'none' }}>
              <SearchForProducts
                redirectTo={pathname?.includes('/bundles') ? 'bundles' : 'browse'}
                onSearchCallback={() => setIsMenuOpen(false)}
                showDropdown={false}
              />
            </Box>
            <Flex
              marginTop={{ base: '30px', lg: '0' }}
              direction="column"
              justifyContent="space-between"
              backgroundColor="white"
              height="calc(100% - 68px)"
            >
              {routes.map((route) => (
                <Link href={route.link} key={route.link} passHref legacyBehavior>
                  <StyledLink isDisabled={route.isProtected && !isLoggedIn} onClick={() => setIsMenuOpen(false)}>
                    {t(route.label)}
                  </StyledLink>
                </Link>
              ))}

              {!isUserInNewsletter && (
                <StyledLink isDisabled={false} onClick={handleNewsletterLinkClick}>
                  {t('newsletter')}
                </StyledLink>
              )}

              {language === 'no' && (
                <StyledLink isDisabled={false} onClick={() => handleLanguageChange('en')}>
                  {t('changeToEnglish')}
                </StyledLink>
              )}
              {language === 'en' && (
                <StyledLink isDisabled={false} onClick={() => handleLanguageChange('no')}>
                  {t('changeToNorwegian')}
                </StyledLink>
              )}

              <Divider />

              <Box>
                {isLoggedIn && (
                  <button onClick={logout}>
                    <Text
                      fontSize={{ base: 'md', lg: 'sm' }}
                      color="black"
                      textAlign="left"
                      pb={{ base: '20px', lg: '0' }}
                    >
                      {t('logout')}
                    </Text>
                  </button>
                )}

                {!isLoggedIn && (
                  <Flex
                    onClick={() => setIsMenuOpen(false)}
                    justifyContent="space-between"
                    alignItems="center"
                    pb={{ base: '20px', lg: '0' }}
                  >
                    <Box width="45%">
                      <ButtonWhite height="40px" href="/login">
                        {t('register')}
                      </ButtonWhite>
                    </Box>
                    <Box width="45%" className="gtm-login-button-menu">
                      <Button height="40px" href="/login">
                        {t('signIn')}
                      </Button>
                    </Box>
                  </Flex>
                )}
              </Box>
            </Flex>
          </StyledMenu>
        )}
      </Box>
    </>
  )
}

export default Menu
